import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImageDropzone } from "@/components/admin/ImageDropzone";
import { useHeroSlides, useInvalidateSiteContent } from "@/hooks/useSiteContent";
import { updateHeroSlides } from "@/lib/api";

const DEFAULT_ALT = "Curated selection of perfumes, groceries, beverages, and chocolates";

function apiErrorDetail(err: unknown, fallback: string): string {
  if (typeof err === "object" && err !== null && "response" in err) {
    const response = (err as { response?: { data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    if (typeof detail === "string") return detail;
  }
  if (typeof err === "object" && err !== null && "code" in err && (err as { code?: string }).code === "ECONNABORTED") {
    return "Request timed out — check that the backend is running";
  }
  return fallback;
}

export const AdminHeroPage = () => {
  const { invalidateHero } = useInvalidateSiteContent();
  const { data: slides, isLoading, isError } = useHeroSlides();
  const heroSlide = slides?.[0] ?? null;

  const [alt, setAlt] = useState(DEFAULT_ALT);
  const [initialized, setInitialized] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (slides && !initialized) {
      setAlt(heroSlide?.alt || DEFAULT_ALT);
      setInitialized(true);
    }
  }, [slides, heroSlide?.alt, initialized]);

  const handleUpload = async (results: { url: string; public_id?: string }[]) => {
    const result = results[0];
    if (!result) return;

    setError(null);
    setSaved(false);
    setUploading(true);
    try {
      await updateHeroSlides([
        {
          url: result.url,
          public_id: result.public_id ?? null,
          alt,
          sort_order: 0,
        },
      ]);
      await invalidateHero();
      setSaved(true);
    } catch (err: unknown) {
      setError(apiErrorDetail(err, "Failed to save hero image"));
    } finally {
      setUploading(false);
    }
  };

  const onSubmitAlt = async (e: FormEvent) => {
    e.preventDefault();
    if (!heroSlide) return;

    setError(null);
    setSubmitting(true);
    try {
      await updateHeroSlides([{ ...heroSlide, alt }]);
      await invalidateHero();
      setSaved(true);
    } catch (err: unknown) {
      setError(apiErrorDetail(err, "Failed to save alt text"));
    } finally {
      setSubmitting(false);
    }
  };

  const busy = uploading || submitting;

  return (
    <div data-testid="admin-hero-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Hero image</h1>
        <p className="text-sm text-vg-gray mt-1">
          Upload the main image shown in the landing page hero section below the headline.
        </p>
      </div>

      {isLoading && (
        <p className="text-sm text-vg-gray mb-4">Loading current hero image…</p>
      )}

      {isError && (
        <p className="text-sm text-amber-700 mb-4">
          Could not load the current hero image. You can still upload a new one below.
        </p>
      )}

      {error && (
        <p className="text-sm text-red-600 mb-4" data-testid="admin-form-error">
          {error}
        </p>
      )}

      {saved && !error && (
        <p className="text-sm text-green-700 mb-4">Hero image saved.</p>
      )}

      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6 max-w-5xl">
        <div className="bg-white border border-black/10 p-6 space-y-4">
          <div className="aspect-[16/9] border border-black/10 bg-[#f3f4f6] overflow-hidden">
            {heroSlide?.url ? (
              <img
                src={heroSlide.url}
                alt={heroSlide.alt}
                className="w-full h-full object-cover object-center"
                data-testid="admin-hero-preview"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-vg-gray px-6 text-center">
                No hero image yet. Upload one below to replace the default storefront image.
              </div>
            )}
          </div>

          <ImageDropzone
            folder="hero"
            disabled={busy}
            label={
              heroSlide
                ? "Drop a new image to replace, or click to browse"
                : "Upload hero image"
            }
            onError={setError}
            onUploaded={handleUpload}
          />

          {uploading && <p className="text-sm text-vg-gray">Uploading and saving…</p>}
        </div>

        <div className="bg-white border border-black/10 p-6 h-fit">
          <form onSubmit={onSubmitAlt} className="space-y-4">
            <label className="block space-y-1">
              <span className="text-sm font-medium">Alt text</span>
              <textarea
                className="w-full min-h-24 px-3 py-2 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black resize-y"
                value={alt}
                onChange={(e) => {
                  setAlt(e.target.value);
                  setSaved(false);
                }}
                disabled={!heroSlide || busy}
              />
              <span className="text-xs text-vg-gray">
                Describe the image for accessibility and SEO.
              </span>
            </label>

            <button
              type="submit"
              disabled={!heroSlide || busy}
              className="h-10 w-full px-4 bg-vg-navy text-white text-sm font-medium hover:bg-vg-cyan disabled:opacity-50"
              data-testid="admin-hero-save-alt"
            >
              {submitting ? "Saving…" : "Save alt text"}
            </button>
          </form>

          {heroSlide?.url && (
            <p className="text-xs text-vg-gray mt-4 break-all">{heroSlide.url}</p>
          )}
        </div>
      </div>

      <div className="mt-8">
        <Link
          to="/"
          className="inline-flex items-center h-10 px-4 border border-black/15 text-sm hover:bg-[#f8fafc]"
        >
          View storefront
        </Link>
      </div>
    </div>
  );
};

export default AdminHeroPage;
